import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { timeEntriesAPI, projectsAPI, tasksAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Play, Square, Plus, Trash2, Clock as ClockIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const TimeTracking = () => {
  const { user } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [showTimerDialog, setShowTimerDialog] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [manualFormData, setManualFormData] = useState({
    project_id: '',
    task_id: '',
    description: '',
    start_time: '',
    duration_minutes: ''
  });
  const [timerFormData, setTimerFormData] = useState({
    project_id: '',
    task_id: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let interval;
    if (activeTimer) {
      // Calculate initial elapsed time
      const start = new Date(activeTimer.start_time);
      const now = new Date();
      setElapsedTime(Math.floor((now - start) / 1000));
      
      // Update every 60 seconds (show in minutes)
      interval = setInterval(() => {
        const start = new Date(activeTimer.start_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        setElapsedTime(diff);
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const loadData = async () => {
    try {
      const [entriesRes, projectsRes] = await Promise.all([
        timeEntriesAPI.getAll({}),
        projectsAPI.getAll()
      ]);
      setTimeEntries(entriesRes.data);
      setProjects(projectsRes.data);
      const active = entriesRes.data.find(e => !e.end_time && e.user_id === user.id);
      setActiveTimer(active || null);
      if (active) {
        const tasksRes = await tasksAPI.getAll(active.project_id);
        setTasks(tasksRes.data);
      }
    } catch (error) {
      toast.error('Failed to load time entries');
    }
  };

  const handleProjectChange = async (projectId, isManual = false) => {
    try {
      const tasksRes = await tasksAPI.getAll(projectId);
      setTasks(tasksRes.data);
      if (isManual) {
        setManualFormData({ ...manualFormData, project_id: projectId, task_id: '' });
      } else {
        setTimerFormData({ ...timerFormData, project_id: projectId, task_id: '' });
      }
    } catch (error) {
      console.error('Failed to load tasks');
    }
  };

  const handleStartTimer = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...timerFormData,
        start_time: new Date().toISOString(),
        task_id: timerFormData.task_id || null
      };
      await timeEntriesAPI.create(payload);
      toast.success('Timer started');
      setShowTimerDialog(false);
      setTimerFormData({ project_id: '', task_id: '', description: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to start timer');
    }
  };

  const handleStopTimer = async () => {
    if (!activeTimer) return;
    try {
      const now = new Date();
      const start = new Date(activeTimer.start_time);
      const durationMinutes = Math.floor((now - start) / 1000 / 60);
      await timeEntriesAPI.update(activeTimer.id, {
        end_time: now.toISOString(),
        duration_minutes: durationMinutes
      });
      toast.success('Timer stopped');
      setActiveTimer(null);
      setElapsedTime(0);
      loadData();
    } catch (error) {
      toast.error('Failed to stop timer');
    }
  };

  const handleForceStopTimer = async (entryId) => {
    if (!window.confirm('Sigur vrei să oprești forțat acest timer?')) return;
    try {
      await timeEntriesAPI.forceStop(entryId);
      toast.success('Timer oprit forțat');
      setActiveTimer(null);
      setElapsedTime(0);
      loadData();
    } catch (error) {
      toast.error('Eroare la oprirea forțată a timer-ului');
    }
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    try {
      const startTime = new Date(manualFormData.start_time);
      const endTime = new Date(startTime.getTime() + parseInt(manualFormData.duration_minutes) * 60000);
      const payload = {
        project_id: manualFormData.project_id,
        task_id: manualFormData.task_id || null,
        description: manualFormData.description,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        duration_minutes: parseInt(manualFormData.duration_minutes),
        is_manual: true
      };
      await timeEntriesAPI.create(payload);
      toast.success('Time entry added');
      setShowManualDialog(false);
      setManualFormData({ project_id: '', task_id: '', description: '', start_time: '', duration_minutes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add time entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await timeEntriesAPI.delete(entryId);
      toast.success('Entry deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    // Display only hours and minutes, not seconds
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0h 0m';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-4xl font-bold tracking-tight" data-testid="time-tracking-title">Time Tracking</h1>
            <p className="mt-2 text-muted-foreground">Track your time with timers or manual entries</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="manual-entry-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Manual Entry
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-outfit text-2xl">Add Manual Entry</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleManualEntry} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="manual_project">Project *</Label>
                    <Select value={manualFormData.project_id} onValueChange={(value) => handleProjectChange(value, true)}>
                      <SelectTrigger data-testid="manual-project-select">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="manual_task">Task</Label>
                    <Select value={manualFormData.task_id} onValueChange={(value) => setManualFormData({ ...manualFormData, task_id: value })}>
                      <SelectTrigger data-testid="manual-task-select">
                        <SelectValue placeholder="Select task (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {tasks.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="manual_description">Description</Label>
                    <Textarea
                      id="manual_description"
                      value={manualFormData.description}
                      onChange={(e) => setManualFormData({ ...manualFormData, description: e.target.value })}
                      rows={2}
                      data-testid="manual-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="manual_start_time">Start Time *</Label>
                      <Input
                        id="manual_start_time"
                        type="datetime-local"
                        value={manualFormData.start_time}
                        onChange={(e) => setManualFormData({ ...manualFormData, start_time: e.target.value })}
                        required
                        data-testid="manual-start-time-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="manual_duration">Duration (minutes) *</Label>
                      <Input
                        id="manual_duration"
                        type="number"
                        min="1"
                        value={manualFormData.duration_minutes}
                        onChange={(e) => setManualFormData({ ...manualFormData, duration_minutes: e.target.value })}
                        required
                        data-testid="manual-duration-input"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowManualDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-brand-orange hover:bg-brand-orange/90" data-testid="submit-manual-entry-btn">
                      Add Entry
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {!activeTimer && (
              <Dialog open={showTimerDialog} onOpenChange={setShowTimerDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-brand-orange hover:bg-brand-orange/90" data-testid="start-timer-btn">
                    <Play className="mr-2 h-4 w-4" />
                    Start Timer
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-2xl">Start Timer</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleStartTimer} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="timer_project">Project *</Label>
                      <Select value={timerFormData.project_id} onValueChange={(value) => handleProjectChange(value, false)} required>
                        <SelectTrigger data-testid="timer-project-select">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="timer_task">Task</Label>
                      <Select value={timerFormData.task_id} onValueChange={(value) => setTimerFormData({ ...timerFormData, task_id: value })}>
                        <SelectTrigger data-testid="timer-task-select">
                          <SelectValue placeholder="Select task (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {tasks.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="timer_description">Description</Label>
                      <Textarea
                        id="timer_description"
                        value={timerFormData.description}
                        onChange={(e) => setTimerFormData({ ...timerFormData, description: e.target.value })}
                        rows={2}
                        data-testid="timer-description-input"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowTimerDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-brand-orange hover:bg-brand-orange/90" data-testid="submit-timer-btn">
                        Start Tracking
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {activeTimer && (
          <Card className="border-brand-orange" data-testid="active-timer-card">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-brand-orange flex items-center justify-center">
                    <ClockIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">Timer Running</p>
                    <p className="text-sm text-muted-foreground">{activeTimer.description || 'No description'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="font-mono text-4xl font-bold" data-testid="timer-display">{formatTime(elapsedTime)}</div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleStopTimer}
                      variant="destructive"
                      data-testid="stop-timer-btn"
                    >
                      <Square className="mr-2 h-4 w-4" />
                      Stop Timer
                    </Button>
                    <Button
                      onClick={() => handleForceStopTimer(activeTimer.id)}
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      data-testid="force-stop-timer-btn"
                      title="Folosește dacă butonul Stop nu funcționează"
                    >
                      Force Stop
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle className="text-xl font-semibold">Time Entries</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/20 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Project</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Start Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.filter(e => e.user_id === user.id).map((entry) => {
                    const project = projects.find(p => p.id === entry.project_id);
                    return (
                      <tr key={entry.id} className="border-b border-border hover:bg-muted/50" data-testid="time-entry-row">
                        <td className="px-4 py-3 text-sm font-medium">{project?.name || 'Unknown'}</td>
                        <td className="px-4 py-3 text-sm">{entry.description || '-'}</td>
                        <td className="px-4 py-3 text-sm font-mono">
                          {new Date(entry.start_time).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {entry.end_time ? formatDuration(entry.duration_minutes) : <span className="text-brand-orange">Running</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium uppercase tracking-wide ${entry.is_manual ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                            {entry.is_manual ? 'Manual' : 'Timer'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {entry.end_time && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => handleDeleteEntry(entry.id)}
                              data-testid="delete-entry-btn"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {timeEntries.filter(e => e.user_id === user.id).length === 0 && (
                <div className="py-12 text-center text-muted-foreground">
                  No time entries yet. Start tracking your time!
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};